import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import useFetchProfile from "../Charterer/lib/useFetchProfile";
import axios from "axios";

const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

if (!SECRET_KEY) {
  throw new Error("REACT_APP_ENCRYPTION_KEY is not set");
}

export const AuthContext = createContext({
  isAuthenticated: false,
  userType: "",
  user: null,
  role: "",
  token: null,
  login: () => {},
  logout: () => {},
  setUserType: () => {},
  vesselType: "",
});

export const AuthProvider = ({ children }) => {
  const [vesselType, setVesselType] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isAuthenticated") === "true"
  );
  const [userType, setUserType] = useState(
    localStorage.getItem("userType") || ""
  );
  const [role, setRole] = useState(localStorage.getItem("role") || "");
  const [token, setToken] = useState(() => {
    const encryptedToken = Cookies.get("sessionData");
    if (encryptedToken) {
      try {
        return CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY).toString(
          CryptoJS.enc.Utf8
        );
      } catch (e) {
        console.error("Failed to decrypt sessionData:", e);
        return "";
      }
    }
    return "";
  });

  const { profileData, profileError, profileLoading } = useFetchProfile(
    token,
    userType
  );

  useEffect(() => {
    if (profileData && !profileData.role) {
      profileData.role = role;
    }
  }, [profileData, role]);

  const login = useCallback(
    (user) => {
      setIsAuthenticated(true);
      setUserType(userType);
      setRole(user.role);
      const encryptedToken = CryptoJS.AES.encrypt(
        user.token,
        SECRET_KEY
      ).toString();
      setToken(user.token);
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("userType", userType);
      localStorage.setItem("role", user.role);
      Cookies.set("sessionData", encryptedToken);
    },
    [userType]
  );

  const getLogoutEndpoint = (userType) => {
    if (userType === "Charterer") {
      return `${process.env.REACT_APP_SERVER_URL}/auth/logout/charterer`;
    } else if (userType === "Vessel Owner") {
      return `${process.env.REACT_APP_SERVER_URL}/auth/logout/vesselOwner`;
    }
    return null;
  };

  const logout = useCallback(async () => {
    const endpoint = getLogoutEndpoint(userType);
    if (endpoint) {
      try {
        await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        console.error("Failed to log out from the server:", error);
      }
    }

    setIsAuthenticated(false);
    setRole("");
    setToken("");
    localStorage.removeItem("isAuthenticated")
    localStorage.removeItem("role");
    Cookies.remove("sessionData");
  }, [userType, token]);

  const setUserTypeContext = useCallback((newUserType) => {
    setUserType(newUserType);
  }, []);

  useEffect(() => {
    localStorage.setItem("isAuthenticated", String(isAuthenticated));
    localStorage.setItem("userType", userType);
    localStorage.setItem("role", role);
    if (token) {
      try {
        const encryptedToken = CryptoJS.AES.encrypt(
          token,
          SECRET_KEY
        ).toString();
        Cookies.set("sessionData", encryptedToken);
      } catch (e) {
        console.error("Failed to encrypt sessionData:", e);
        Cookies.remove("sessionData");
      }
    } else {
      Cookies.remove("sessionData");
    }
  }, [isAuthenticated, userType, role, token]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      userType,
      user: profileData,
      role,
      token,
      login,
      logout,
      setUserType: setUserTypeContext,
      profileData,
      profileError,
      profileLoading,
      vesselType,
      setVesselType,
    }),
    [
      isAuthenticated,
      userType,
      role,
      token,
      profileData,
      profileError,
      profileLoading,
      login,
      logout,
      setUserTypeContext,
      vesselType,
      setVesselType,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



